import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface CommandPaletteItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  keywords?: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandPaletteItem[];
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, items }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized)
    {
      return items;
    }

    return items.filter((item) => {
      const haystack = `${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`.toLowerCase();
      return haystack.includes(normalized);
    });
  }, [items, query]);

  useEffect(() => {
    if (!open)
    {
      return;
    }

    setQuery('');
    setActiveIndex(0);
    const timer = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open)
  {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown')
    {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp')
    {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter')
    {
      event.preventDefault();
      const selected = filtered[activeIndex];
      if (selected)
      {
        selected.onSelect();
        onClose();
      }
      return;
    }

    if (event.key === 'Escape')
    {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/40 flex items-start justify-center pt-[12vh] px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Buscar ou executar um comando"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar módulos, ações ou documentos…"
            className="flex-1 outline-none text-[15px] text-gray-800 placeholder-gray-400"
          />
          <kbd className="text-[11px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        <div className="max-h-[360px] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              Nenhum resultado para “{query}”.
            </div>
          ) : (
            filtered.map((item, index) => {
              const previous = filtered[index - 1];
              const showGroup = !previous || previous.group !== item.group;
              const isActive = index === activeIndex;

              return (
                <React.Fragment key={item.id}>
                  {showGroup && (
                    <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                      {item.group}
                    </div>
                  )}
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      item.onSelect();
                      onClose();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-[14px] transition-colors ${
                      isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className={isActive ? 'text-blue-600' : 'text-gray-400'}>{item.icon}</span>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hint && <span className="text-[12px] text-gray-400">{item.hint}</span>}
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
